import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const { user, logout } = useAuth();
  const location = useLocation();
  const userMenuRef = useRef(null);

  // Handle Scroll Effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location]);

  // Handle Click Outside User Menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (isUserMenuOpen && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen]);

  return (
    <header
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#0F0C29]/95' : 'bg-glass-bg'
        } backdrop-blur-[20px] border-b border-glass-border`}
    >
      <nav className="max-w-[1400px] mx-auto flex items-center justify-between px-8 py-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="BESST"
            className="h-12 w-auto object-contain"
          />
          <span className="font-extrabold text-xl text-white tracking-widest hidden sm:block" style={{ textShadow: '0 0 20px rgba(252, 211, 77, 0.5)' }}>
            BESST
          </span>
        </Link>

        {/* Desktop Navigation */}
        <ul className="hidden md:flex items-center gap-8">
          <li>
            <Link to="/" className="nav-link">Home</Link>
          </li>
          <li>
            <Link to="/courses" className="nav-link">Courses</Link>
          </li>

          <li>
            <Link to="/current-affairs" className="nav-link">Current Affairs</Link>
          </li>
          <li>
            <Link to="/subscriptions" className="nav-link">Subscriptions</Link>
          </li>
          <li>
            <Link to="/blog" className="nav-link">Blog</Link>
          </li>
        </ul>

        {/* Auth Buttons / User Menu */}
        <div className="flex items-center gap-4">
          {user ? (
            <div className="flex items-center gap-3">
              <Link
                to="/dashboard"
                className="subscription-btn relative flex items-center gap-2 text-sm"
              >
                <span>📊</span>
                <span>Dashboard</span>
              </Link>
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-1 text-white/70 hover:text-white transition-colors text-sm"
                  title={user.firstName}
                >
                  <span className="hidden sm:inline max-w-[80px] truncate">{user.firstName}</span>
                  <span className="text-xs">▼</span>
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 bg-[#1a0b2e] border border-glass-border rounded-xl p-4 min-w-[200px] animate-slide-down shadow-xl z-50">
                    <div className="border-b border-white/10 pb-2 mb-2">
                      <p className="text-sm text-gray-400">Signed in as</p>
                      <p className="font-bold text-white truncate">{user.email}</p>
                    </div>
                    <Link to="/settings" className="block text-white hover:text-accent-gold py-2 transition-colors">
                      <span className="mr-2">⚙️</span> Settings
                    </Link>
                    <Link to="/dashboard" className="block text-white hover:text-accent-gold py-2 transition-colors">
                      <span className="mr-2">📊</span> Dashboard
                    </Link>
                    <button onClick={logout} className="block text-red-400 hover:text-red-300 py-2 w-full text-left transition-colors">
                      <span className="mr-2">🚪</span> Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login" className="btn-auth">
                <span>Sign In</span>
              </Link>
              <Link to="/register" className="subscription-btn relative">
                Get Started
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse-slow">
                  New
                </span>
              </Link>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white text-2xl p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-glass-bg backdrop-blur-[20px] border-t border-glass-border p-8 animate-slide-down">
          <ul className="space-y-4 mb-8">
            <li>
              <Link to="/" className="block text-text-light font-medium text-lg hover:text-accent-gold transition-colors">
                Home
              </Link>
            </li>
            <li>
              <Link to="/courses" className="block text-text-light font-medium text-lg hover:text-accent-gold transition-colors">
                Courses
              </Link>
            </li>

            <li>
              {/* Changed from Blogs to Current Affairs to match Desktop */}
              <Link to="/current-affairs" className="block text-text-light font-medium text-lg hover:text-accent-gold transition-colors">
                Current Affairs
              </Link>
            </li>
            <li>
              <Link to="/subscriptions" className="block text-text-light font-medium text-lg hover:text-accent-gold transition-colors">
                Subscriptions
              </Link>
            </li>
            <li>
              <Link to="/blog" className="block text-text-light font-medium text-lg hover:text-accent-gold transition-colors">
                Blog
              </Link>
            </li>
          </ul>

          {!user && (
            <div className="flex flex-col gap-4">
              <Link to="/login" className="btn-auth text-center">Sign In</Link>
              <Link to="/register" className="subscription-btn text-center">Get Started</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;